import React, { useState } from "react";
import * as Icons from "lucide-react";
import { useApp } from "@/context/AppContext";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { PageHeader, SectionCard, StatusBadge, Mono, InfoNote, ProvenanceBadge } from "@/components/uikit";
import { GENESIS_HASH } from "@/lib/crypto";
import { toast } from "sonner";

const TELEMETRY = [
  { key: "raw_drift", label: "raw_drift (°)", def: 2.14 },
  { key: "corrected_offset", label: "corrected_offset (°)", def: 0.29 },
  { key: "latency", label: "latency (ms)", def: 42 },
  { key: "jitter", label: "jitter (ms)", def: 3.5 },
  { key: "confidence", label: "confidence (0-1)", def: 0.86 },
];

export default function StateCapture() {
  const { records, integrity, anchorEvent } = useApp();
  const initial = TELEMETRY.reduce((a, f) => ({ ...a, [f.key]: f.def }), {});
  const [tele, setTele] = useState(initial);
  const [note, setNote] = useState("");
  const [busy, setBusy] = useState(false);

  const stateRecords = records.filter((r) => r.payload_type === "System State" || r.telemetry);

  const capture = async () => {
    setBusy(true);
    try {
      const clean = {};
      TELEMETRY.forEach((f) => (clean[f.key] = Number(tele[f.key])));
      const payload = JSON.stringify({ observation: clean, sequence: records.length }, null, 2);
      const rec = await anchorEvent({
        payload,
        payload_type: "System State",
        layer: 4,
        notes: note || "Sequential state observation",
        drift_status: clean.raw_drift >= 2 ? "WARNING" : "NONE",
        source_tag: "local",
        telemetry: clean,
      });
      toast.success("Observation captured", { description: `${rec.event_id} · references previous hash` });
      setNote("");
    } finally {
      setBusy(false);
    }
  };

  return (
    <div>
      <PageHeader overline="Sequential Observations" title="State Capture" icon="Camera"
        subtitle="Save sequential observations. Each automatically references the SHA-256 of the previous record, forming a local cryptographic chain." />

      <div className="grid grid-cols-1 gap-6 lg:grid-cols-3">
        <SectionCard title="Capture Observation" icon="CameraOff" testId="statecapture-form">
          <div className="space-y-3">
            {TELEMETRY.map((f) => (
              <div key={f.key}>
                <Label htmlFor={`sc-${f.key}`} className="mb-1 block text-[11px] text-slate-400">{f.label}</Label>
                <Input id={`sc-${f.key}`} data-testid={`statecapture-${f.key}`} type="number" step="0.01"
                  value={tele[f.key]} onChange={(e) => setTele((s) => ({ ...s, [f.key]: e.target.value }))}
                  className="h-9 border-slate-700 bg-slate-950 font-mono text-xs text-cyan-200" />
              </div>
            ))}
            <div>
              <Label htmlFor="sc-note" className="mb-1 block text-[11px] text-slate-400">Notes</Label>
              <Input id="sc-note" data-testid="statecapture-note" value={note} onChange={(e) => setNote(e.target.value)}
                className="h-9 border-slate-700 bg-slate-950 text-slate-200" placeholder="Optional context" />
            </div>
            <Button onClick={capture} disabled={busy} className="w-full bg-cyan-500 font-bold text-slate-950 hover:bg-cyan-400" data-testid="statecapture-submit">
              {busy ? <Icons.Loader2 className="mr-2 h-4 w-4 animate-spin" /> : <Icons.Camera className="mr-2 h-4 w-4" />} Capture State
            </Button>
            <InfoNote tone="info">Each observation chains to the previous record via <Mono>previous_hash</Mono>.</InfoNote>
          </div>
        </SectionCard>

        <SectionCard title="Observation Chain Topology" icon="GitBranch" className="lg:col-span-2" testId="statecapture-topology">
          {records.length === 0 ? (
            <p className="py-6 text-center text-sm text-slate-500">No observations captured yet.</p>
          ) : (
            <div className="space-y-0">
              {records.map((r, i) => {
                const res = integrity[i];
                const ok = res && res.selfOk && res.linkOk;
                return (
                  <div key={r.id} data-testid={`state-node-${r.id}`}>
                    <div className={`flex items-start gap-3 rounded-md border p-3 ${ok ? "border-slate-800 bg-slate-950/50" : "border-rose-700 bg-rose-950/30"}`}>
                      <span className="flex h-8 w-8 shrink-0 items-center justify-center rounded-full border border-cyan-800 bg-slate-950 font-mono text-xs font-bold text-cyan-300">{i}</span>
                      <div className="min-w-0 flex-1">
                        <div className="flex flex-wrap items-center gap-2">
                          <span className="text-sm font-semibold text-slate-200">{r.payload_type}</span>
                          <StatusBadge status={ok ? "VERIFIED" : "DRIFT"} />
                          <ProvenanceBadge source={r.source_tag} />
                        </div>
                        <p className="mt-1 truncate font-mono text-[11px] text-cyan-300">hash: {r.sha256}</p>
                        <p className="truncate font-mono text-[11px] text-slate-500">prev: {r.previous_hash === GENESIS_HASH ? "GENESIS (0×64)" : r.previous_hash}</p>
                      </div>
                    </div>
                    {i < records.length - 1 && (
                      <div className="ml-4 flex h-5 items-center">
                        <Icons.ArrowDown className="h-4 w-4 text-slate-700" aria-hidden="true" />
                      </div>
                    )}
                  </div>
                );
              })}
            </div>
          )}
        </SectionCard>
      </div>
    </div>
  );
}
